import React, { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import ShareWithFriendsButton from 'components/Common/ShareWithFriendsButton'
import LastMinuteSummary from './LastMinuteSummary'
import OfferBNPLAd from '../OfferBNPLAd'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import TextButton from 'components/Luxkit/Button/TextButton'
import OfferBadgeTag from 'components/Common/OfferBadgeTag'
import BodyText from 'components/Luxkit/Typography/BodyText'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import SolidPlayIcon from 'components/Luxkit/Icons/solid/SolidPlayIcon'
import LastMinutePropertyDetails from './LastMinutePropertyDetails'
import { OptimizelyClientSideExperiments, OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import LastMinutePropertyDetailsExperimentVariant from './LastMinutePropertyDetailsExperimentVariant'
import PropertyMapV2 from 'components/Common/PropertyMap/PropertyMapV2'

const OfferIntroGrid = styled(LayoutContainer)`
  display: grid;
  padding: ${rem(20)};
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    padding: ${rem(20)} ${rem(40)};
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(30)} ${rem(12)};
    grid-gap: ${rem(16)} ${rem(40)};
    grid-template-columns: 1fr min-content;
    grid-template-rows: min-content min-content min-content min-content 1fr;
  }
`

const OfferBadgeIntroWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-row: 4;

  ${mediaQueryUp.tablet} {
    grid-row: 3;
    grid-column: 1;
  }
`

const OfferBadgeTagSection = styled(BodyText)`

  > * {
    margin: 0;
  }
`

const BNPLAd = styled(OfferBNPLAd)`
  margin-top: ${rem(24)};
  padding-top: ${rem(24)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.five};

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const SummaryBlock = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    position: sticky;
    /* height of the filter bar */
    top: ${rem(64)};
    display: block;
    align-self: flex-start;
    grid-row: 3;
    grid-column: 2;
  }

  ${mediaQueryUp.desktop} {
    grid-row: 2 / span 3;
    grid-column: 2;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(LastMinuteSummary)`
  display: block;
  align-self: flex-start;
  min-width: ${rem(290)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(370)};
  }
`

const OfferOtherActions = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  grid-row: 3;
  grid-column: 1;

  ${mediaQueryUp.tablet} {
    justify-content: flex-end;
    grid-row: 1;
    grid-column: 2;
  }
`

const WatchVideoButton = styled(TextButton)`
  margin-top: ${rem(12)};
`

interface Props {
  offer: App.Offer;
  customerPortalURL: string;
  regionCode: string;
  pathname: string;
  memberId: string;
  onWatchVideo: () => void;
  className?: string;
  onViewPackageOptions: () => void;
  showSocialWidget: boolean;
  flightDestination?: App.Airport;
}

function LastMinuteIntro(props: Props) {
  const {
    offer,
    onWatchVideo,
    memberId,
    pathname,
    regionCode,
    customerPortalURL,
    className,
    onViewPackageOptions,
    flightDestination,
  } = props

  const offerPageTruncateText = !!useOptimizelyExperiment(OptimizelyServerSideExperiments.offerPageTruncateText)
  const offerPageMapInSummary = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.offerPageMapInSummary)

  const property = offer.property
  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')

  const shareConfig = getShareModalConfig({
    customerPortalURL,
    image: offer.images[0],
    memberId,
    offerId: offer.id,
    offerLocation: location,
    pathname,
    region: regionCode,
    type: 'offer',
    utmCampaign: 'webshare',
  })

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])

  return (
    <OfferIntroGrid className={className} data-testid="offer-intro">
      <OfferUrgencyLabels offer={offer} showLimitedAvailability />
      <OfferOtherActions>
        <TripGuard inverse>
          <ShareModal shareConfig={shareConfig}>
            <ShareWithFriendsButton outdent="right" />
          </ShareModal>
        </TripGuard>
        <TripGuard>
          <ShareModal shareConfig={shareConfig}>
            <ShareWithFriendsButton />
          </ShareModal>
          <BookmarkButton offer={offer} />
        </TripGuard>
      </OfferOtherActions>

      <OfferBadgeIntroWrapper>
        {offer.badge && <OfferBadgeTagSection variant="medium">
          <OfferBadgeTag badge={offer.badge} />
        </OfferBadgeTagSection>}
        {offerPageTruncateText && <LastMinutePropertyDetailsExperimentVariant
          offer={offer}
          property={property}
          location={location}
        />}
        {!offerPageTruncateText && <LastMinutePropertyDetails
          offer={offer}
          property={property}
          location={location}
        />}
        {offer.vimeoVideoId &&
          <WatchVideoButton kind="secondary" startIcon={ <SolidPlayIcon />} onClick={onWatchVideo}>
            Play video
          </WatchVideoButton>
        }
      </OfferBadgeIntroWrapper>
      <SummaryBlock>
        <div>
          <HotelSummary onViewPackageOptions={onViewPackageOptions} flightDestination={flightDestination} />
        </div>
        <OfferBNPLAd prices={allPrices} />
        <div>
          {offerPageMapInSummary && property && <PropertyMapV2 offer={offer} />}
        </div>
      </SummaryBlock>
      <BNPLAd prices={allPrices} />
    </OfferIntroGrid>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer,
  regionCode: appState.geo.currentRegionCode,
  pathname: appState.router.location.pathname,
  customerPortalURL: appState.config.customerPortalURL,
  memberId: appState.auth.account.memberId,
  showSocialWidget: appState.ui.socialWidget.display,
}))(LastMinuteIntro)
